import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import {
    FilterBarGroup,
    getDefaultFilterGroupDef,
    IFilterGroupDef
} from "@components/smart/smartFilterBar/SmartFilterBar.types";
import { IReportHierarchy } from "@components/smart/smartTable";
import { TCellValue } from "@components/table";
import { IFormatOptions } from "@odata/OData.utils";
import { CommonReportProps } from "@pages/reports/CommonDefs";
import {
    composedDateRangeOnBeforeLoadCallback,
    getComposedDateRangeFieldsDefs
} from "@pages/reports/customFilterComponents/ComposedDateRange";
import {
    getEmployeeNumberOursCommonDef,
    getPayslipCommonDef,
    payslipInitialSortBy
} from "@pages/reports/payrollOverview/PayrollOverview.utils";
import { setReportDef } from "@pages/reports/Report.test.utils";
import { ReportStorage } from "@pages/reports/ReportStorage";
import { isDefined } from "@utils/general";
import i18next from "i18next";

import { TValue } from "../../../global.types";
import { TAX_CREDITS_AND_ALLOWANCES_OVERVIEW_ROUTE } from "../../../routes";
import CurrencyType from "../../../types/Currency";
import { IReportTableDefinition, TReportColumnOverrides } from "../Report.utils";
import { ReportId } from "../ReportIds";

export const getDefinition = (): IReportTableDefinition => {
    const tableId = ReportId.AdvanceAndWithholdingTax;
    const title = i18next.t("Reporting:AdvanceAndWithholdingTax.Title");
    const path = "AdvanceAndWithholdingTax";
    
    const filterBarDef: IFilterGroupDef[] = [{
        ...getDefaultFilterGroupDef(FilterBarGroup.Parameters),
        defaultFilters: [
            CommonReportProps.dateRange,
            CommonReportProps.dateRangeCustomValue,
        ],
        filterDefinition: {
            ...getComposedDateRangeFieldsDefs(tableId, true),
        },
    }, {
        ...getDefaultFilterGroupDef(FilterBarGroup.Filters),
        allowCustomFilters: false,
        defaultFilters: [],
        filterDefinition: {}
    }];


    const onBeforeLoad = async (storage: ReportStorage) => {
        await composedDateRangeOnBeforeLoadCallback(storage, false, true, true);
    };

    const parameters: string[] = [CommonReportProps.dateRange, CommonReportProps.dateRangeCustomValue];
    const defaultReportHierarchy: IReportHierarchy = {
        "Aggregate": false,
        "Groups": [],
        "Columns": [
            {
                "ColumnAlias": "YearMonth",
            },
            {
                "ColumnAlias": "PrEmployee_NumberOurs",
            },
            {
                "ColumnAlias": "Name",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzAdvanceIncomeTaxCalcBasis",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzAdvanceIncomeTaxCalcBasisRound",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzAdvanceIncomeTaxGross",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzIncomeTaxBenefits",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzIncomeTaxNetAdvance",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzIncomeTaxWithholdingCalcBasis",
            },
            {
                "ColumnAlias": "PrPayslipEmploymentItem_CzIncomeTaxNetWithholding",
            }
        ],
        "Aggregations": []
    };


    const columnOverrides: TReportColumnOverrides = {
        YearMonth: getPayslipCommonDef(),
        PrEmployee_NumberOurs: getEmployeeNumberOursCommonDef(),
        PrPayslipEmploymentItem_CzIncomeTaxBenefits: {
            formatter: (val: TValue, args: IFormatOptions): TCellValue => {
                if (!isDefined(val)) {
                    return null;
                }

                const formattedVal = CurrencyType.format(val, { currency: args.entity["CurrencyCode"] });

                if (val === 0) {
                    return formattedVal;
                }

                return getTableDrillDownLink(formattedVal, {
                    route: TAX_CREDITS_AND_ALLOWANCES_OVERVIEW_ROUTE,
                    context: args.storage.context,
                    filters: {
                        [CommonReportProps.dateRange]: args.storage.data.entity[CommonReportProps.dateRange],
                        [CommonReportProps.dateRangeCustomValue]: args.storage.data.entity[CommonReportProps.dateRangeCustomValue],
                        "YearMonth": args.entity["YearMonth"],
                        "PrEmployee_NumberOurs": args.entity["PrEmployee_NumberOurs"]
                    },
                    storage: args.storage
                });
            }
        }
    };

    const initialSortBy = payslipInitialSortBy;


    return {
        title,
        path,
        id: tableId,
        initialSortBy,
        columnOverrides,
        filterBarDef,
        onBeforeLoad,
        parameters,
        defaultReportHierarchy
    };
};

setReportDef(ReportId.AdvanceAndWithholdingTax, getDefinition);